// ── Anonymous visitor fingerprint ─────────────────────────────────────────────
// Used to (a) give unauthenticated visitors a stable identity so the backend
// can rate-limit guest chat sessions, and (b) resume the same anonymous
// conversation across reloads. The fingerprint is derived from passive browser
// signals and cached in localStorage so it stays stable on this device.

const FINGERPRINT_KEY = "enga-anonymous-fingerprint"

/** Backend contract: `^an_[a-zA-Z0-9_-]{8,128}$` or the request is rejected. */
const FINGERPRINT_PATTERN = /^an_[a-zA-Z0-9_-]{8,128}$/

/** Min length of the entropy part after `an_`; keeps ids well inside the charset window. */
const MIN_ENTROPY_LENGTH = 12

function stableHash(input: string): string {
  let hash = 0
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i)
    hash |= 0
  }
  return (hash >>> 0).toString(36)
}

/** Enforce the backend charset — base36 yields only `[a-z0-9]`. */
function safeEntropy(source: string): string {
  return source.replace(/[^a-z0-9_-]/g, "0")
}

/** Pad a short hash up to the minimum so `an_...` always has ≥8 chiral chars. */
function paddedEntropy(hash: string): string {
  if (hash.length >= MIN_ENTROPY_LENGTH) return hash
  return hash.padEnd(MIN_ENTROPY_LENGTH, "0")
}

function collectSignals(): string {
  const nav = typeof navigator !== "undefined" ? navigator : null
  const win = typeof window !== "undefined" ? window : null
  const parts = [
    nav?.userAgent ?? "",
    nav?.language ?? "",
    nav?.languages?.join(",") ?? "",
    nav?.platform ?? "",
    win?.screen?.width ?? "",
    win?.screen?.height ?? "",
    win?.screen?.colorDepth ?? "",
    win?.devicePixelRatio ?? "",
    Intl.DateTimeFormat().resolvedOptions().timeZone ?? "",
    new Date().getTimezoneOffset(),
    nav?.hardwareConcurrency ?? "",
    (win as Window & { performance?: { timing?: { fetchStart?: number } } })?.performance?.timing?.fetchStart ?? "",
  ]
  return parts.join("|")
}

/**
 * Return (and lazily create) a stable anonymous fingerprint for this browser.
 * Falls back to a per-tab random id so the app never crashes if storage is
 * unavailable or signals are missing.
 *
 * The id always matches `^an_[a-zA-Z0-9_-]{8,128}$` — the backend 401s anything
 * that doesn't.
 */
export function getAnonymousFingerprint(): string {
  if (typeof window === "undefined") return ""

  try {
    const cached = window.localStorage.getItem(FINGERPRINT_KEY)
    if (cached && FINGERPRINT_PATTERN.test(cached)) return cached

    const timeEntropy = Date.now().toString(36)
    // Base36 signals are always safe; pad so the post-`an_` part clears 8 chars.
    const signals = paddedEntropy(safeEntropy(stableHash(collectSignals())))
    const fingerprint = `an_${signals}_${timeEntropy.slice(-4)}`

    window.localStorage.setItem(FINGERPRINT_KEY, fingerprint)
    return fingerprint
  } catch {
    return `an_${paddedEntropy(Math.random().toString(36).slice(2)).slice(0, 16)}`
  }
}

/**
 * Return the cached anonymous fingerprint for this browser, or null when there
 * is none / it doesn't match the backend format. Read-only — used at handoff
 * time so guests can claim their conversations after signing in without having
 * the id re-generated between the guest session and the claim call.
 */
export function getStoredAnonymousFingerprint(): string | null {
  if (typeof window === "undefined") return null
  try {
    const cached = window.localStorage.getItem(FINGERPRINT_KEY)
    return cached && FINGERPRINT_PATTERN.test(cached) ? cached : null
  } catch {
    return null
  }
}